import { openAxios } from 'services/axios';
import { LOCALIZATION_BASE_URL, OPEN } from 'services/consts';

import i18n from 'utils/localization';

export const initializedLanguages = [];

// IndexedDB setup
const DB_NAME = 'app-language-resources';
const DB_VERSION = 1;
const RESOURCE_STORE = 'languageResources';
const METADATA_STORE = 'metadata';

// Open database connection
const openDB = () => {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);

		request.onerror = (event) => reject(event.target.error);
		request.onsuccess = (event) => resolve(event.target.result);

		request.onupgradeneeded = (event) => {
			const db = event.target.result;

			// Create stores if they don't exist
			if (!db.objectStoreNames.contains(RESOURCE_STORE)) {
				db.createObjectStore(RESOURCE_STORE, { keyPath: 'language' });
			}

			if (!db.objectStoreNames.contains(METADATA_STORE)) {
				db.createObjectStore(METADATA_STORE, { keyPath: 'key' });
			}
		};
	});
};

// Get resource from IndexedDB
const getFromDB = async (storeName, key) => {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const transaction = db.transaction(storeName, 'readonly');
		const store = transaction.objectStore(storeName);
		const request = store.get(key);

		request.onerror = (event) => reject(event.target.error);
		request.onsuccess = () => resolve(request.result);
	});
};

// Save to IndexedDB
const saveToDB = async (storeName, data) => {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const transaction = db.transaction(storeName, 'readwrite');
		const store = transaction.objectStore(storeName);
		const request = store.put(data);

		request.onerror = (event) => reject(event.target.error);
		request.onsuccess = () => resolve(request.result);
	});
};

const fetchLanguageResource = async (language) => {
	// this is a workaround to clear the localStorage for the language resources
	try {
		localStorage.removeItem('engValue_resource');
		localStorage.removeItem('engValue_resource_fetch_date');
		localStorage.removeItem('trValue_resource');
		localStorage.removeItem('trValue_resource_fetch_date');
		localStorage.removeItem('arabicValue_resource');
		localStorage.removeItem('arabicValue_resource_fetch_date');
	} catch {
		// do nothing
	}

	if (initializedLanguages.includes(language)) return;

	// Get existing language resource from IndexedDB
	const resourceData = await getFromDB(RESOURCE_STORE, language);
	const languageResource = resourceData?.resource || {};

	// Get fetch date metadata
	const fetchDateObj = await getFromDB(METADATA_STORE, `${language}_fetch_date`);
	const fetchDate = fetchDateObj?.value;

	// Fetch from server
	const response = await openAxios.get(
		`${LOCALIZATION_BASE_URL}${OPEN.GET_LANGUAGE_RESOURCE}?lang=${language}${
			fetchDate ? `&timestamp=${fetchDate}` : ''
		}`
	);

	const serverFetchDate = response.headers['x-fetch-date'];

	// Merge existing resources with new data
	const updatedResource = {
		...languageResource,
		...response.data
	};

	// Save updated resource
	await saveToDB(RESOURCE_STORE, {
		language,
		resource: updatedResource
	});

	// Save fetch date if available
	if (serverFetchDate) {
		await saveToDB(METADATA_STORE, {
			key: `${language}_fetch_date`,
			value: serverFetchDate
		});
	}

	// Add to i18n
	i18n.addResourceBundle(language, 'translation', updatedResource, true, true);

	initializedLanguages.push(language);
};

export default fetchLanguageResource;
